import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Search, Map as MapI, List as ListI, Plus, MapPin, Phone, Globe } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapPicker from "@/components/admin/MapPicker";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41],
});

export default function CityGuide() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [view, setView] = useState("list");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", address: "", phone: "", description: "", website: "", lat: null, lng: null });

  const PAGE_SIZE = 60;

  const load = async (nextSkip = 0) => {
    if (!selected?.id) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({
        province_id: selected.id,
        limit: PAGE_SIZE,
        skip: nextSkip,
      });
      if (q) p.append("search", q);
      if (cat) p.append("category", cat);
      const r = await api.get(`/city-guide?${p}`);
      const list = r.data.items || [];
      setTotal(r.data.total || 0);
      setItems((prev) => (nextSkip === 0 ? list : [...prev, ...list]));
      setSkip(nextSkip);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setSkip(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const loadMore = () => load(skip + PAGE_SIZE);
  const runSearch = () => { setSkip(0); load(0); };

  // When switching to map view, auto-fetch all remaining pages so every
  // pin is visible — otherwise the map would only show the first 60 items.
  const loadAllRemaining = async () => {
    if (!selected?.id || items.length >= total) return;
    setLoading(true);
    try {
      let cur = items.length;
      let acc = [...items];
      while (cur < total) {
        const p = new URLSearchParams({
          province_id: selected.id,
          limit: 200,
          skip: cur,
        });
        if (q) p.append("search", q);
        if (cat) p.append("category", cat);
        // eslint-disable-next-line no-await-in-loop
        const r = await api.get(`/city-guide?${p}`);
        const list = r.data.items || [];
        if (!list.length) break;
        acc = [...acc, ...list];
        cur += list.length;
        if (cur > 5000) break; // safety cap
      }
      setItems(acc);
      setSkip(cur);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === "map") loadAllRemaining();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const cats = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))], [items]);
  const mapItems = items.filter((e) => e.lat && e.lng);
  const center = mapItems[0] ? [mapItems[0].lat, mapItems[0].lng] : [39.0, 35.0];

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Öneri için giriş yapmalısınız");
    try {
      const payload = {
        ...form,
        province_id: selected.id,
        lat: form.lat != null ? Number(form.lat) : null,
        lng: form.lng != null ? Number(form.lng) : null,
      };
      await api.post("/city-guide/submit", payload);
      toast.success("Öneriniz alındı, onaydan sonra yayınlanacak");
      setShowForm(false);
      setForm({ name: "", category: "", address: "", phone: "", description: "", website: "", lat: null, lng: null });
    } catch { toast.error("Gönderilemedi"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black">Şehir Rehberi — {selected?.name}</h1>
          <p className="text-gray-500 text-sm">İşletme, kurum ve hizmet noktaları</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${view === "list" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}><ListI className="w-4 h-4" /> Liste</button>
          <button onClick={() => setView("map")} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1 ${view === "map" ? "bg-[#e11d2a] text-white" : "border border-gray-200"}`}><MapI className="w-4 h-4" /> Harita</button>
          <button onClick={() => setShowForm(!showForm)} className="px-3 py-1.5 text-sm rounded-md bg-[#0b0b0d] text-white flex items-center gap-1"><Plus className="w-4 h-4" /> Öner</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center mb-4">
        <div className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-2 bg-white flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Ara..." className="flex-1 outline-none text-sm" />
        </div>
        <select value={cat} onChange={(e) => { const v = e.target.value; setCat(v); setSkip(0); const p = new URLSearchParams({ province_id: selected.id, limit: PAGE_SIZE, skip: 0 }); if (q) p.append("search", q); if (v) p.append("category", v); api.get(`/city-guide?${p}`).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); }); }} className="px-3 py-2 border border-gray-200 rounded-md text-sm">
          <option value="">Tüm kategoriler</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={runSearch} className="px-4 py-2 text-sm bg-[#e11d2a] text-white rounded-md">Ara</button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gray-100 rounded-lg p-5 mb-5 grid md:grid-cols-2 gap-3">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="İşletme / Kurum adı" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Kategori" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adres" className="px-3 py-2 border border-gray-200 rounded-md text-sm md:col-span-2" />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefon" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Web" className="px-3 py-2 border border-gray-200 rounded-md text-sm" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Açıklama" rows={3} className="px-3 py-2 border border-gray-200 rounded-md text-sm md:col-span-2"></textarea>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Konum (haritaya tıklayarak pin yerleştirin)
            </label>
            <MapPicker
              lat={form.lat}
              lng={form.lng}
              onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
              height={280}
              defaultQuery={form.address || form.name || selected?.name || ""}
            />
            <p className="text-[11px] text-gray-500 mt-1">İsteğe bağlı. Pin koyarsanız işletme haritada gösterilir.</p>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-md">İptal</button>
            <button type="submit" data-testid="cityguide-submit-btn" className="px-4 py-2 text-sm bg-[#e11d2a] text-white rounded-md">Gönder</button>
          </div>
        </form>
      )}

      {view === "map" ? (
        <div className="h-[500px] rounded-lg overflow-hidden border">
          <MapContainer center={center} zoom={11} scrollWheelZoom={false} className="w-full h-full">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {mapItems.map((e) => (
              <Marker key={e.id} position={[e.lat, e.lng]} icon={icon}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-bold">{e.name}</div>
                    <div className="text-xs text-gray-600">{e.category}</div>
                    <div className="text-xs">{e.address}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      ) : (
        <>
          <div className="text-xs text-gray-500 mb-2" data-testid="cityguide-count">
            {items.length} / {total} kayıt gösteriliyor
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {items.map((e) => (
              <div key={e.id} className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="text-[10px] uppercase font-bold text-[#e11d2a] mb-1">{e.category}</div>
                <h3 className="font-bold">{e.name}</h3>
                <p className="text-xs text-gray-600 mt-1">{e.description}</p>
                <div className="flex flex-col gap-1 mt-2 text-xs text-gray-500">
                  {e.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.address}</span>}
                  {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 text-[#e11d2a]"><Phone className="w-3 h-3" /> {e.phone}</a>}
                  {e.website && <a href={e.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {e.website}</a>}
                </div>
              </div>
            ))}
          </div>
          {items.length < total && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                data-testid="cityguide-load-more"
                className="px-6 py-2 border border-gray-200 rounded-md text-sm font-semibold hover:bg-gray-50 hover:border-[#e11d2a] hover:text-[#e11d2a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Yükleniyor…" : "Daha Fazla Yükle"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
